import type { InferType } from "yup"
import { object, string, boolean, array, number } from "yup"

import { FormInputType } from "~graphql/generated/types"
import type { CreateFormInput, UpdateFormInput } from "~graphql/generated/types"
import type {
    FormFieldsFragment,
    FormInputFieldsFragment,
} from "~graphql/common.generated"
import COPY_CONSTANTS from "~config/copy-constants"

import type { CustomFieldInput } from "./custom-field-form-input-helpers"
import { removeTempIds } from "./create-form-helpers"

const VALIDATION_TEST_KEYS = {
    SIGNATURE_REQUIRES_EMAIL: "signature-requires-email",
}

export const CREATE_FORM_BASICS_FORM_KEYS = {
    NAME: "name",
    DESCRIPTION: "description",
    TEMPLATE: "template",
} as const

export const CREATE_FORM_DETAILS_FORM_KEYS = {
    SHOULD_ADD_FORM_TO_BOOKING: "should_add_form_to_booking",
    SHOULD_CREATE_LEAD_FROM_SUBMISSION: "should_create_lead_from_submission",
    SUCCESS_REDIRECT_URL: "success_redirect_url",
    LISTING_IDS: "listing_ids",
    PRICING_PACKAGE_IDS: "pricing_package_ids",
    FORM_INPUTS: "form_inputs",
    IS_REQUIRED: "is_required",
    HAS_TO_BE_FILLED_PER_MEMBER: "has_to_be_filled_per_member",
    CUSTOM_FIELD_TITLE: "custom_field_title",
    CUSTOM_FIELD_TYPE: "custom_field_type",
    CUSTOM_FIELD_IS_REQUIRED: "custom_field_is_required",
    CUSTOM_FIELD_PLACEHOLDER: "custom_field_placeholder",
    CUSTOM_FIELD_OPTIONS: "custom_field_options",
    CUSTOM_FIELD_ADDITIONAL_NOTES: "custom_field_additional_notes",
    CUSTOM_FIELD_SHOULD_ALLOW_PRICING_PACKAGE_SELECTION:
        "custom_field_should_allow_pricing_package_selection",
    TEMPLATE: "template",
} as const

const formInputsSchema = array()
    .of(
        object({
            id: string().required(),
            input_type: string().oneOf(Object.values(FormInputType)).required(),
            title: string().required(),
            is_required: boolean().required(),
            options: array().of(string().required()),
            placeholder: string(),
            additional_notes: string(),
            order: number(),
        })
    )
    .test(
        VALIDATION_TEST_KEYS.SIGNATURE_REQUIRES_EMAIL,
        COPY_CONSTANTS.SIGNATURE_REQUIRES_EMAIL,
        validateSignatureRequiresEmail
    )

export function getFormEditorDefaultValues({
    form,
}: {
    form: FormFieldsFragment | undefined
}) {
    const listings = form?.link_to_booking_configs?.listings ?? []
    const pricingPackages =
        form?.link_to_booking_configs?.pricing_packages ?? []
    const shouldAddFormToBooking =
        listings.length > 0 || pricingPackages.length > 0

    const formattedFormInputs =
        form?.form_inputs?.map((input) => ({
            id: input.id,
            input_type: input.input_type,
            title: input.title,
            is_required: input.is_required,
            options: input.options ?? undefined,
            placeholder: input.placeholder ?? undefined,
            additional_notes: input.additional_notes ?? undefined,
            order: input.order ?? undefined,
            should_allow_pricing_package_selection:
                input.should_allow_pricing_package_selection ?? undefined,
        })) ?? []

    return {
        [CREATE_FORM_BASICS_FORM_KEYS.NAME]: form?.name ?? "",
        [CREATE_FORM_BASICS_FORM_KEYS.DESCRIPTION]: form?.description ?? "",
        [CREATE_FORM_DETAILS_FORM_KEYS.SHOULD_ADD_FORM_TO_BOOKING]:
            shouldAddFormToBooking,
        [CREATE_FORM_DETAILS_FORM_KEYS.SHOULD_CREATE_LEAD_FROM_SUBMISSION]:
            form?.should_create_lead_from_submission ?? false,
        [CREATE_FORM_DETAILS_FORM_KEYS.SUCCESS_REDIRECT_URL]:
            form?.success_redirect_url ?? "",
        [CREATE_FORM_DETAILS_FORM_KEYS.LISTING_IDS]: listings,
        [CREATE_FORM_DETAILS_FORM_KEYS.PRICING_PACKAGE_IDS]: pricingPackages,
        [CREATE_FORM_DETAILS_FORM_KEYS.FORM_INPUTS]: formattedFormInputs,
        [CREATE_FORM_DETAILS_FORM_KEYS.IS_REQUIRED]: form?.is_required ?? false,
        [CREATE_FORM_DETAILS_FORM_KEYS.HAS_TO_BE_FILLED_PER_MEMBER]:
            form?.link_to_booking_configs?.has_to_be_filled_per_member ?? false,
        [CREATE_FORM_DETAILS_FORM_KEYS.CUSTOM_FIELD_TITLE]: "",
        [CREATE_FORM_DETAILS_FORM_KEYS.CUSTOM_FIELD_TYPE]: FormInputType.Text,
        [CREATE_FORM_DETAILS_FORM_KEYS.CUSTOM_FIELD_IS_REQUIRED]: false,
        [CREATE_FORM_DETAILS_FORM_KEYS.CUSTOM_FIELD_PLACEHOLDER]: "",
    }
}

export function getFormEditorFormSchema() {
    return object({
        [CREATE_FORM_BASICS_FORM_KEYS.NAME]: string().required(),
        [CREATE_FORM_BASICS_FORM_KEYS.DESCRIPTION]: string().nullable(),
        [CREATE_FORM_DETAILS_FORM_KEYS.TEMPLATE]: string().optional(),
        [CREATE_FORM_DETAILS_FORM_KEYS.SHOULD_ADD_FORM_TO_BOOKING]: boolean(),
        [CREATE_FORM_DETAILS_FORM_KEYS.SHOULD_CREATE_LEAD_FROM_SUBMISSION]:
            boolean(),
        [CREATE_FORM_DETAILS_FORM_KEYS.SUCCESS_REDIRECT_URL]: string()
            .trim()
            .url(COPY_CONSTANTS.FORM_COPY.SUCCESS_REDIRECT_URL_ERROR)
            .nullable()
            .optional(),
        [CREATE_FORM_DETAILS_FORM_KEYS.LISTING_IDS]: array().of(
            string().required()
        ),
        [CREATE_FORM_DETAILS_FORM_KEYS.PRICING_PACKAGE_IDS]: array().of(
            string().required()
        ),
        [CREATE_FORM_DETAILS_FORM_KEYS.FORM_INPUTS]: formInputsSchema,
        [CREATE_FORM_DETAILS_FORM_KEYS.IS_REQUIRED]: boolean(),
        [CREATE_FORM_DETAILS_FORM_KEYS.HAS_TO_BE_FILLED_PER_MEMBER]: boolean(),
        [CREATE_FORM_DETAILS_FORM_KEYS.CUSTOM_FIELD_TITLE]: string().optional(),
        [CREATE_FORM_DETAILS_FORM_KEYS.CUSTOM_FIELD_TYPE]: string(),
        [CREATE_FORM_DETAILS_FORM_KEYS.CUSTOM_FIELD_IS_REQUIRED]:
            boolean().default(false),
    })
}

export type FormEditorFormData = InferType<
    ReturnType<typeof getFormEditorFormSchema>
>

export function formatFormEditorFormData(
    data: FormEditorFormData
): CreateFormInput {
    return {
        name: data[CREATE_FORM_BASICS_FORM_KEYS.NAME],
        description: data[CREATE_FORM_BASICS_FORM_KEYS.DESCRIPTION],
        ...formatFormEditorDetailsFields(data),
    }
}

function formatFormEditorDetailsFields(
    data: FormEditorFormData
): Pick<
    UpdateFormInput,
    | "form_inputs"
    | "is_required"
    | "should_create_lead_from_submission"
    | "success_redirect_url"
    | "link_to_booking_configs"
> {
    const shouldAddFormToBooking =
        data[CREATE_FORM_DETAILS_FORM_KEYS.SHOULD_ADD_FORM_TO_BOOKING] || false
    const listings = shouldAddFormToBooking
        ? data[CREATE_FORM_DETAILS_FORM_KEYS.LISTING_IDS] || []
        : []
    const pricingPackages = shouldAddFormToBooking
        ? data[CREATE_FORM_DETAILS_FORM_KEYS.PRICING_PACKAGE_IDS] || []
        : []

    const formattedData: Pick<
        UpdateFormInput,
        | "form_inputs"
        | "is_required"
        | "should_create_lead_from_submission"
        | "success_redirect_url"
        | "link_to_booking_configs"
    > = {
        form_inputs: formatFormInputsForMutation(data),
        is_required: data[CREATE_FORM_DETAILS_FORM_KEYS.IS_REQUIRED],
        should_create_lead_from_submission:
            data[
                CREATE_FORM_DETAILS_FORM_KEYS.SHOULD_CREATE_LEAD_FROM_SUBMISSION
            ] || false,
        success_redirect_url: formatSuccessRedirectUrl(data),
    }

    if (listings.length > 0 || pricingPackages.length > 0) {
        formattedData.link_to_booking_configs = {
            listings,
            pricing_packages: pricingPackages,
            has_to_be_filled_per_member:
                data[
                    CREATE_FORM_DETAILS_FORM_KEYS.HAS_TO_BE_FILLED_PER_MEMBER
                ] || false,
        }
    }

    return formattedData
}

function formatFormInputsForMutation(data: FormEditorFormData) {
    const formInputs = data[CREATE_FORM_DETAILS_FORM_KEYS.FORM_INPUTS] ?? []

    return removeTempIds(formInputs)
}

function formatSuccessRedirectUrl(data: FormEditorFormData): string | null {
    const successRedirectUrl =
        data[CREATE_FORM_DETAILS_FORM_KEYS.SUCCESS_REDIRECT_URL]?.trim()

    return successRedirectUrl || null
}

export function getHasInputByInputType({
    formInputs,
    inputType,
    isRequired,
}: {
    formInputs?: FormInputFieldsFragment[] | CustomFieldInput[]
    inputType: FormInputType
    isRequired?: boolean
}) {
    if (isRequired !== undefined) {
        return Boolean(
            formInputs?.find(
                (input) =>
                    input.input_type === inputType &&
                    input.is_required === isRequired
            )
        )
    }
    return Boolean(formInputs?.find((input) => input.input_type === inputType))
}

function validateSignatureRequiresEmail(
    formInputs: FormInputFieldsFragment[] | CustomFieldInput[] | undefined
): boolean {
    if (!formInputs) return true

    const hasSignatureInput = getHasInputByInputType({
        formInputs,
        inputType: FormInputType.Signature,
    })

    if (!hasSignatureInput) return true

    return getHasInputByInputType({
        formInputs,
        inputType: FormInputType.Email,
        isRequired: true,
    })
}
